'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

export default function Custom404NotFound() {
  const lines = React.useMemo(() => [
    { text: '404', delay: 0 },
    { text: 'PAGE', delay: 1.5 },
    { text: 'NOT', delay: 2 },
    { text: 'FOUND', delay: 2.5 }
  ], []);

  // Variants for falling letters
  const letterVariants = {
    initial: {
      y: 0,
      rotateX: 0,
      opacity: 1,
      scale: 1,
    },
    animate: {
      y: [0, 100, 800],
      rotateX: [0, 60, 120],
      opacity: [1, 1, 0],
      scale: [1, 1.1, 0.8],
      transition: {
        duration: 2,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
  };

  // Background reveal animation
  const backgroundVariants = {
    initial: {
      opacity: 0,
      scale: 1.1,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 4,
        duration: 1,
        ease: 'easeOut',
      },
    },
  };

  // Button and content reveal animation
  const contentVariants = {
    initial: {
      opacity: 0,
      y: 50,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 4.5,
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="relative w-screen h-screen bg-gray-900 overflow-hidden flex items-center justify-center text-white">
      {/* Main animation layer */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
        {lines.map(({ text, delay }, lineIndex) => (
          <div
            key={`line-${lineIndex}`}
            className="flex justify-center items-center min-h-[25vh]"
          >
            {text.split('').map((char, charIndex) => (
              <motion.div
                key={`char-${lineIndex}-${charIndex}`}
                className="font-mono text-[15vw] md:text-[20vw] font-extrabold leading-none select-none"
                variants={letterVariants}
                initial="initial"
                animate="animate"
                transition={{
                  delay: delay + charIndex * 0.1,
                  duration: 2,
                }}
              >
                {char}
              </motion.div>
            ))}
          </div>
        ))}
      </div>

      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0 z-10"
        variants={backgroundVariants}
        initial="initial"
        animate="animate"
        aria-hidden="true"
      >
        <div className="w-full h-full bg-gradient-to-br from-indigo-900 via-purple-700 to-indigo-400" />
      </motion.div>

      {/* Content that appears after animation */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center z-30"
        variants={contentVariants}
        initial="initial"
        animate="animate"
        role="alert"
        aria-label="404 error message"
      >
        <div className="text-center space-y-8">
          <h1 className="text-3xl md:text-5xl font-bold">
            You seem to have hit a dead end!
          </h1>
          <p className="text-lg md:text-xl text-indigo-200">
            Don&apos;t worry, I can guide you back home.
          </p>
          <Link
            href="/"
            className="inline-block px-8 py-4 bg-white text-indigo-900 text-lg font-bold rounded-lg
                     hover:bg-indigo-100 transform hover:scale-105 transition-all duration-200
                     focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-900"
            role="button"
            aria-label="Return to home page"
          >
            Return to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
}